import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import { useDispatch } from 'react-redux';
import { CommonDialog, CommonDialogHeader } from '../../components/Dialog';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import {
  CustomerAccountReactivationService,
  IDeactivatedUser,
} from '../../api/services/customerAccountReactivationService';

export const CustomerAccountReactivationPage = () => {
  const dispatch = useDispatch();

  const [phone, setPhone] = useState('');
  const [results, setResults] = useState<IDeactivatedUser[]>([]);
  const [searched, setSearched] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IDeactivatedUser | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);

  const handleSearch = async () => {
    if (!phone.trim()) return;
    try {
      const data = await CustomerAccountReactivationService.searchDeactivatedByPhone(phone.trim());
      setResults(data);
      setSearched(true);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to search for deactivated accounts.',
          severity: 'error',
        }),
      );
    }
  };

  const handleReactivateClick = (user: IDeactivatedUser) => {
    setSelectedUser(user);
    setConfirmOpen(true);
  };

  const handleConfirmReactivate = async () => {
    if (!selectedUser) return;
    setIsReactivating(true);
    try {
      await CustomerAccountReactivationService.reactivateUser(selectedUser.user_pkid);
      dispatch(showNotification({ message: 'Account has been reactivated.', severity: 'success' }));
      setConfirmOpen(false);
      setSelectedUser(null);
      setResults([]);
      setSearched(false);
      setPhone('');
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to reactivate account.',
          severity: 'error',
        }),
      );
    } finally {
      setIsReactivating(false);
    }
  };

  return (
    <Box maxWidth={600}>
      {/* Search bar */}
      <Box display="flex" gap={1} mb={3}>
        <TextField
          fullWidth
          placeholder="Enter phone number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
          size="small"
        />
        <Button variant="contained" onClick={handleSearch} disabled={!phone.trim()}>
          Search
        </Button>
      </Box>

      {/* No results */}
      {searched && results.length === 0 && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={1}
          py={6}
          color="text.secondary"
        >
          <PersonOffIcon sx={{ fontSize: 48, opacity: 0.4 }} />
          <Typography variant="body2">
            No deactivated account found for this phone number.
          </Typography>
        </Box>
      )}

      {/* Result cards */}
      {results.map((user) => (
        <Card key={user.user_pkid} variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography fontWeight={600}>
                  {user.user_fname} {user.user_lname}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.user_phone}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.user_email}
                </Typography>
              </Box>
              <Button variant="contained" size="small" onClick={() => handleReactivateClick(user)}>
                Reactivate
              </Button>
            </Box>
          </CardContent>
        </Card>
      ))}

      {/* Confirmation dialog */}
      <CommonDialog
        isOpen={confirmOpen}
        header={
          <CommonDialogHeader title="Reactivate Account" onClose={() => setConfirmOpen(false)} />
        }
        body={
          <Box mt={1}>
            <Typography variant="body2">
              Are you sure you want to reactivate the account for{' '}
              <strong>
                {selectedUser?.user_fname} {selectedUser?.user_lname}
              </strong>
              ?
            </Typography>
          </Box>
        }
        footer={
          <Box display="flex" gap={1} justifyContent="flex-end" mt={2}>
            <Button
              variant="outlined"
              onClick={() => setConfirmOpen(false)}
              disabled={isReactivating}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={handleConfirmReactivate} disabled={isReactivating}>
              {isReactivating ? 'Reactivating...' : 'Confirm'}
            </Button>
          </Box>
        }
      />
    </Box>
  );
};
